import styles from './PaymentManageDefaultPage.module.scss'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { ArrowsUpDownIcon } from '@heroicons/react/24/solid'
import { useEffect, useMemo, useState } from 'react'
import Pagination from '../../components/Pagination/Pagination'
import { useNavigate } from 'react-router-dom'
import { useSettlements, useUser } from '../../hooks'

const ITEMS_PER_PAGE = 5
const PERIOD_CONTRACT_TYPES = new Set(['year_second_half', 'year_first_half', 'both'])
const PAYMENT_STATUS_MAP = {
	SETTLEMENT_PENDING: '정산전',
	WAITING_FOR_PAYMENT: '입금대기',
	PAYMENT_COMPLETED: '입금완료',
	PAYMENT_DISBURSED: '지급완료',
}

const formatDateString = (value) => {
	if (!value) return null
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return null

	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')

	return `${year}-${month}-${day}`
}

const toTimestamp = (value) => {
	if (!value) return null
	const date = new Date(value)
	const time = date.getTime()
	return Number.isNaN(time) ? null : time
}

const resolveProfileType = (profileType) => {
	if (profileType === 'tourCaddie') return '투어캐디'
	if (profileType === 'tourPro') return '투어프로'
	return ''
}

const resolvePeriodTitle = (contractType) => {
	if (contractType === 'year_second_half') return '기간계약(하반기)'
	if (contractType === 'year_first_half') return '기간계약(상반기)'
	if (contractType === 'both') return '기간계약(상반기/하반기)'
	return ''
}

const PaymentManageDefaultPage = () => {
	const [tabIndex, setTabIndex] = useState('전체')
	const [sortIndex, setSortIndex] = useState('최신순')
	const [currentPage, setCurrentPage] = useState(1)
	const navigate = useNavigate()
	const { data: currentUser } = useUser()
	const { data: settlementContracts = [], isLoading, isError } = useSettlements()

	const processedItems = useMemo(() => {
		if (!Array.isArray(settlementContracts)) return []

		return settlementContracts.map((item, index) => {
			const contractPaymentId = item?.id || `${index + 1}`
			const contract = item?.contract ?? {}
			const contractTour = item?.contractTour ?? {}
			const tour = contractTour?.tour ?? {}
			const player = contract?.player ?? {}
			const caddie = contract?.caddie ?? {}

			const contractType = contract?.contract_type
			const isPeriodContract =
				PERIOD_CONTRACT_TYPES.has(contractType) || item?.payment_type === 'PERIOD'
			const paymentStatus = PAYMENT_STATUS_MAP[item?.paymentStatus] || '에러'

			const currentUserId = currentUser?.id
			const currentUserType = currentUser?.userType

			const isCurrentUserPlayer =
				(currentUserId && player?.id && currentUserId === player.id) ||
				currentUserType === 'tourPro'

			const counterpart = isCurrentUserPlayer ? caddie : player
			const counterpartTypeCode =
				counterpart?.user_type ||
				counterpart?.profile_type ||
				(isCurrentUserPlayer ? 'tourCaddie' : 'tourPro')
			const type = resolveProfileType(counterpartTypeCode)

			const counterpartName =
				counterpart?.name || (isCurrentUserPlayer ? caddie?.name : player?.name) || ''

			const fallbackProfileImage =
				counterpartTypeCode === 'tourPro' || counterpartTypeCode === 'tourPro'.toUpperCase()
					? '/images/pro-default.png'
					: '/images/caddie-default-profile.svg'
			const counterpartyProfileImage = counterpart?.profile_image || fallbackProfileImage

			const title =
				tour?.name ||
				resolvePeriodTitle(contractType) ||
				(contractType ? contractType : '정산 항목')

			const dateStart = tour?.started_at || contract?.contract_started_at
			const dateEnd = tour?.ended_at || contract?.contract_ended_at
			const formattedStart = formatDateString(dateStart)
			const formattedEnd = formatDateString(dateEnd)

			return {
				id: contractPaymentId,
				listOrder: index + 1,
				type,
				title,
				caddieName: contract?.caddie?.name || '',
				counterpartyName: counterpartName,
				counterpartyType: type,
				counterpartyProfileImage,
				tourDate:
					formattedStart && formattedEnd ? `${formattedStart} ~ ${formattedEnd}` : '-',
				status: paymentStatus,
				statusCode: item?.paymentStatus,
				paymentType: item?.payment_type,
				startDateValue: toTimestamp(dateStart),
				endDateValue: toTimestamp(dateEnd),
				isPeriodContract,
				original: item,
			}
		})
	}, [settlementContracts, currentUser])

	const { beforeAdjustCount, beforeDepositCount, afterDepositCount, afterPaymentCount } =
		useMemo(() => {
			const counts = {
				beforeAdjustCount: 0,
				beforeDepositCount: 0,
				afterDepositCount: 0,
				afterPaymentCount: 0,
			}

			processedItems.forEach((item) => {
				switch (item.status) {
					case '정산전':
						counts.beforeAdjustCount += 1
						break
					case '입금대기':
						counts.beforeDepositCount += 1
						break
					case '입금완료':
						counts.afterDepositCount += 1
						break
					case '지급완료':
						counts.afterPaymentCount += 1
						break
					default:
						break
				}
			})

			return counts
		}, [processedItems])

	const filteredItems = useMemo(() => {
		if (tabIndex === '전체') {
			return processedItems
		}
		return processedItems.filter((item) => item.status === tabIndex)
	}, [processedItems, tabIndex])

	const sortedItems = useMemo(() => {
		const items = [...filteredItems]

		items.sort((a, b) => {
			const isLatestSort = sortIndex === '최신순'
			const fallbackValue = isLatestSort ? -Infinity : Infinity
			const aStart = typeof a.startDateValue === 'number' ? a.startDateValue : fallbackValue
			const bStart = typeof b.startDateValue === 'number' ? b.startDateValue : fallbackValue

			if (aStart === bStart) {
				const aEnd = typeof a.endDateValue === 'number' ? a.endDateValue : fallbackValue
				const bEnd = typeof b.endDateValue === 'number' ? b.endDateValue : fallbackValue

				if (!Number.isFinite(aEnd) && !Number.isFinite(bEnd)) {
					return 0
				}

				return isLatestSort ? bEnd - aEnd : aEnd - bEnd
			}

			return isLatestSort ? bStart - aStart : aStart - bStart
		})

		return items
	}, [filteredItems, sortIndex])

	const totalPages = Math.ceil(sortedItems.length / ITEMS_PER_PAGE)
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
	const endIndex = startIndex + ITEMS_PER_PAGE
	const currentItems = sortedItems.slice(startIndex, endIndex)

	useEffect(() => {
		if (sortedItems.length === 0) {
			if (currentPage !== 1) {
				setCurrentPage(1)
			}
			return
		}

		if (currentPage > totalPages) {
			setCurrentPage(totalPages)
		}
	}, [sortedItems.length, totalPages, currentPage])

	const handleItemClick = (item) => {
		if (!item?.id) return
		navigate(`/settlements/${item.id}`)
	}

	const handlePageChange = (page) => {
		setCurrentPage(page)
	}

	return (
		<div className={styles.paymentManageDefaultPageContainer}>
			<div className={styles.paymentManageTitleWrapper}>
				<span className={styles.paymentManageTitle}>정산관리</span>
				<span className={styles.paymentManageSubtitle}>
					정산 내역 및 현황을 확인해보세요
				</span>
			</div>
			<div className={styles.paymentManageStatusDisplayWrapper}>
				<div className={styles.statusDisplayBox}>
					<div className={styles.statusCountBox}>{beforeAdjustCount}건</div>
					<span className={styles.statusLabel}>정산전</span>
					<div className={styles.arrowWrapper}>
						<div className={styles.line}></div>
						<div className={styles.dot_1}></div>
						<ChevronRightIcon className={styles.arrowIcon_1} />
					</div>
				</div>
				<div className={styles.statusDisplayBox}>
					<div className={styles.statusCountBox}>{beforeDepositCount}건</div>
					<span className={styles.statusLabel}>입금대기</span>
					<div className={styles.arrowWrapper}>
						<div className={styles.line}></div>
						<div className={styles.dot_2}></div>
						<ChevronRightIcon className={styles.arrowIcon_2} />
					</div>
				</div>
				<div className={styles.statusDisplayBox}>
					<div className={styles.statusCountBox}>{afterDepositCount}건</div>
					<span className={styles.statusLabel}>입금완료</span>
					<div className={styles.arrowWrapper}>
						<div className={styles.line}></div>
						<div className={styles.dot_3}></div>
						<ChevronRightIcon className={styles.arrowIcon_3} />
					</div>
				</div>
				<div className={styles.statusDisplayBox}>
					<div className={styles.statusCountBox}>{afterPaymentCount}건</div>
					<span className={styles.statusLabel}>지급완료</span>
					<div className={styles.arrowWrapper}>
						<div className={styles.line}></div>
						<div className={styles.dot_4}></div>
					</div>
				</div>
			</div>
			<div className={styles.itemDisplayWrapper}>
				<div className={styles.tabContainer}>
					<span
						className={`${styles.tabItem} ${tabIndex === '전체' ? styles.activeTab : ''}`}
						onClick={() => {
							setTabIndex('전체')
							setCurrentPage(1)
						}}>
						전체
					</span>
					<span
						className={`${styles.tabItem} ${tabIndex === '정산전' ? styles.activeTab : ''}`}
						onClick={() => {
							setTabIndex('정산전')
							setCurrentPage(1)
						}}>
						정산전
					</span>
					<span
						className={`${styles.tabItem} ${tabIndex === '입금대기' ? styles.activeTab : ''}`}
						onClick={() => {
							setTabIndex('입금대기')
							setCurrentPage(1)
						}}>
						입금대기
					</span>
					<span
						className={`${styles.tabItem} ${tabIndex === '입금완료' ? styles.activeTab : ''}`}
						onClick={() => {
							setTabIndex('입금완료')
							setCurrentPage(1)
						}}>
						입금완료
					</span>
					<span
						className={`${styles.tabItem} ${tabIndex === '지급완료' ? styles.activeTab : ''}`}
						onClick={() => {
							setTabIndex('지급완료')
							setCurrentPage(1)
						}}>
						지급완료
					</span>
				</div>
				<div className={styles.sortIconWrapper}>
					<div
						className={styles.sortIconBox}
						onClick={() =>
							setSortIndex(sortIndex === '최신순' ? '오래된순' : '최신순')
						}>
						<ArrowsUpDownIcon className={styles.sortIcon} />
						<span className={styles.sort}>{sortIndex}</span>
					</div>
				</div>
				{isLoading ? (
					<div className={styles.noItems}>
						<p>정산 내역을 불러오는 중입니다.</p>
					</div>
				) : isError ? (
					<div className={styles.noItems}>
						<p>정산 내역을 불러오지 못했습니다.</p>
					</div>
				) : currentItems.length > 0 ? (
					<>
						<div className={styles.itemList}>
							{currentItems.map((item) => (
								<div
									key={item.id}
									className={styles.itemCard}
									onClick={() => handleItemClick(item)}>
									<div className={styles.itemLeftContainer}>
										<div
											className={`${styles.tourTitle} ${
												item.status === '지급완료'
													? styles.tourTitle_gray
													: ''
											}`}>
											{item.title}
										</div>
										<div className={styles.counterpartyInfo}>
											<div className={styles.counterpartyTexts}>
												<span className={styles.counterpartyName}>
													{item.counterpartyName || '-'}
												</span>
												{/* {item.counterpartyType && (
													<span className={styles.counterpartyType}>
														{item.counterpartyType}
													</span>
												)} */}
											</div>
										</div>
										<div className={styles.tourDate}>{item.tourDate}</div>
									</div>
									<div
										className={`${styles.rightContainer} ${styles[item.status]}`}>
										{item.status}
									</div>
								</div>
							))}
						</div>
						{totalPages > 1 && (
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								onPageChange={handlePageChange}
								itemsPerPage={ITEMS_PER_PAGE}
							/>
						)}
					</>
				) : (
					<div className={styles.noItems}>
						<p>해당 조건의 정산 내역이 없습니다.</p>
					</div>
				)}
			</div>
		</div>
	)
}

export default PaymentManageDefaultPage
